import React, { useEffect, useRef, useState, useCallback } from "react";
import "./Projekte.css";
 
const withPublic = (src) =>
  src.startsWith("http") ? src : `${process.env.PUBLIC_URL}/${src.replace(/^\//, "")}`;
 
const carouselItems = [
  { imgSrc: "IMG_1314 2.jpg", alt: "Pflasterarbeiten und Gartenpflege", showOn: "desktop" },
  { imgSrc: "PHOTO-2024-11-04-21-13-09.jpg", alt: "Gartenbau Hannover", showOn: "desktop" },
  { imgSrc: "IMG_8027.jpg", alt: "Gepflegter Rasen und Hecken", showOn: "desktop" },
  { imgSrc: "IMG_1303.jpg", alt: "Gartenpflege – Rasen & Hecken", showOn: "desktop" },
  { imgSrc: "IMG_8022.jpg", alt: "Gartenpflege Hannover", showOn: "desktop" },
  { imgSrc: "9FEDA575-4DA8-41BE-B0D3-F13EFE844D1A_1_201_a.jpeg", alt: "Zaunbau & Pflasterarbeiten", showOn: "desktop" },
  { imgSrc: "IMG_8025.jpg", alt: "Rasen & Heckenarbeiten", showOn: "desktop" },
  { imgSrc: "808a92f7-db59-4643-aa80-80571155ec6a.jpg", alt: "Grünanlagenpflege & Pflaster", showOn: "desktop" },
  { imgSrc: "70E5F2A8-522B-47D3-B50C-8CA63972BB3A_1_201_a.jpeg", alt: "Gartenarbeiten – Pflaster & Zäune", showOn: "desktop" },
  { imgSrc: "IMG_1312 2.jpg", alt: "Terrassenbau & Gartenpflege", showOn: "desktop" },
  { imgSrc: "handy1.jpg", alt: "Gartenbau Hannover", showOn: "desktop" },
  { imgSrc: "handy5.jpg", alt: "Terrassenbau Hannover", showOn: "desktop" },
  { imgSrc: "7ef47808-00aa-481d-b4b3-f80ac2aa95e0.JPG", alt: "Gartenprojekt Hannover", showOn: "desktop" },
  { imgSrc: "5200DA67-C562-4D92-90B0-8B6EB66E692A_1_201_a.jpeg", alt: "EG-Bau Terrasse", showOn: "desktop" },
  { imgSrc: "handy1.JPG", alt: "Gartenpflege & Pflasterarbeiten", showOn: "mobile" },
  { imgSrc: "handy2.JPG", alt: "Natursteinmauer Gartenbau", showOn: "mobile" },
  { imgSrc: "handy3.JPG", alt: "Pflasterarbeiten Einfahrt", showOn: "mobile" },
  { imgSrc: "handy4.JPG", alt: "Gartenpflege & Grünflächen", showOn: "mobile" },
  { imgSrc: "handy5.JPG", alt: "Terrassenbau mit Platten", showOn: "mobile" },
  { imgSrc: "handy6.JPG", alt: "Wegegestaltung Pflastersteine", showOn: "mobile" }
];
 
const FILTERS = [
  { key: "all", label: "Alle Projekte" },
  { key: "pflaster", label: "Pflasterarbeiten" },
  { key: "garten", label: "Gartenpflege" },
  { key: "terrasse", label: "Terrassenbau" },
  { key: "zaun", label: "Zaunbau" },
];
 
// Assign filter tags to desktop items (simple keyword mapping)
const TAG_MAP = [
  "pflaster", "garten", "garten", "garten", "garten",
  "zaun", "garten", "pflaster", "zaun", "terrasse",
  "garten", "terrasse", "garten", "terrasse",
];
 
function useInView(ref, options = {}) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.12, ...options }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}
 
// ── Hero ──────────────────────────────────────────────
function ProjekteHero() {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.3 });
 
  return (
    <header className="projekte-hero" ref={ref}>
      <p className={`projekte-hero-eyebrow${inView ? " visible" : ""}`}>
        EG-Bau · Hannover
      </p>
      <h2 className={`projekte-hero-title${inView ? " visible" : ""}`}>
        Unsere<br /><em>Projekte</em>
      </h2>
      <div className={`projekte-hero-line${inView ? " visible" : ""}`} />
      <p className={`projekte-hero-desc${inView ? " visible" : ""}`}>
        Handwerk, das bleibt. Jedes Projekt erzählt von Leidenschaft
        für Natur, Qualität und langlebige Gestaltung.
      </p>
    </header>
  );
}
 
// ── Desktop Masonry Grid ───────────────────────────────
function MasonryGrid({ items, onCardClick }) {
  const filtersRef = useRef(null);
  const filtersInView = useInView(filtersRef, { threshold: 0.5 });
 
  const [activeFilter, setActiveFilter] = useState("all");
 
  const desktopItems = items.filter((it) => it.showOn === "desktop");
 
  const filtered = desktopItems.filter((_, i) => {
    if (activeFilter === "all") return true;
    return TAG_MAP[i] === activeFilter;
  });
 
  return (
    <>
      {/* Filter Tabs */}
      <nav
        className={`projekte-filters${filtersInView ? " visible" : ""}`}
        ref={filtersRef}
        aria-label="Projekt-Filter"
      >
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`filter-btn${activeFilter === f.key ? " active" : ""}`}
            onClick={() => setActiveFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </nav>
 
      {/* Masonry */}
      <div className="projekte-grid" role="list">
        {filtered.map((item, i) => (
          <GridCard
            key={item.imgSrc + i}
            item={item}
            index={i}
            onClick={() => onCardClick(desktopItems.indexOf(item))}
          />
        ))}
      </div>
    </>
  );
}
 
function GridCard({ item, index, onClick }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const delay = Math.min(index * 80, 500);
 
  return (
    <article
      ref={ref}
      className={`projekte-card${inView ? " visible" : ""}`}
      style={{ transitionDelay: `${delay}ms` }}
      role="listitem"
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      aria-label={item.alt}
    >
      <img
        src={withPublic(item.imgSrc)}
        alt={item.alt}
        loading="lazy"
      />
      <div className="projekte-card-overlay">
        <span className="projekte-card-num">
          {String(index + 1).padStart(2, "0")}
        </span>
        <h3 className="projekte-card-title">{item.alt}</h3>
        <span className="projekte-card-tag">EG-Bau Hannover</span>
      </div>
    </article>
  );
}
 
// ── Lightbox ──────────────────────────────────────────
function Lightbox({ items, currentIndex, onClose, onPrev, onNext }) {
  const isOpen = currentIndex !== null;
  const item = isOpen ? items[currentIndex] : null;
 
  useEffect(() => {
    const handleKey = (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose, onPrev, onNext]);
 
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);
 
  return (
    <div
      className={`projekte-lightbox${isOpen ? " open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Bildvorschau"
    >
      <div
        className="projekte-lightbox-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      {item && (
        <div className="projekte-lightbox-inner">
          <button
            className="projekte-lightbox-close"
            onClick={onClose}
            aria-label="Schließen"
          >
            ✕
          </button>
          <img
            src={withPublic(item.imgSrc)}
            alt={item.alt}
          />
          <div className="projekte-lightbox-meta">
            <span className="projekte-lightbox-counter">
              {currentIndex + 1} / {items.length}
            </span>
            <span style={{ fontSize: "0.8rem", color: "var(--clr-text-muted)" }}>
              {item.alt}
            </span>
          </div>
          <button
            className="projekte-lightbox-nav prev"
            onClick={onPrev}
            aria-label="Vorheriges Bild"
          >
            ←
          </button>
          <button
            className="projekte-lightbox-nav next"
            onClick={onNext}
            aria-label="Nächstes Bild"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}
 
// ── Mobile Carousel (Infinite Loop) ───────────────────
function MobileCarousel({ items }) {
  const mobileItems = items.filter((it) => it.showOn === "mobile");
  const total = mobileItems.length;
 
  // Extended list: [lastClone, slide0, slide1, …, slideN-1, firstClone]
  // Real slides live at positions 1…total. We start at position 1.
  const extendedItems = [mobileItems[total - 1], ...mobileItems, mobileItems[0]];
 
  const [pos, setPos] = useState(1);          // index into extendedItems
  const [anim, setAnim] = useState(true);
  const jumping = useRef(false);
  const wrapRef = useRef(null);               // the visible window div
  const slideRef = useRef(null);              // one real slide to measure width
 
  // realIndex: 0-based index into mobileItems (for dots / counter)
  const realIndex = (pos - 1 + total) % total;
 
  // ── measure actual slide width + gap from DOM ──
  const getStep = useCallback(() => {
    if (!slideRef.current) return 0;
    const style = window.getComputedStyle(slideRef.current.parentElement);
    const gap = parseFloat(style.columnGap || style.gap || "16");
    return slideRef.current.offsetWidth + gap;
  }, []);
 
  // ── move to a position in the extended array ──
  const moveTo = useCallback((nextPos, withAnim = true) => {
    if (jumping.current && withAnim) return;
    setAnim(withAnim);
    setPos(nextPos);
    if (withAnim) jumping.current = true;
  }, []);
 
  // ── after transition: silently snap from clone to real ──
  const onTransitionEnd = useCallback(() => {
    jumping.current = false;
    if (pos === 0) moveTo(total, false);          // was clone-of-last → real last
    else if (pos === total + 1) moveTo(1, false); // was clone-of-first → real first
  }, [pos, total, moveTo]);
 
  const goNext = useCallback(() => moveTo(pos + 1), [pos, moveTo]);
  const goPrev = useCallback(() => moveTo(pos - 1), [pos, moveTo]);
 
  // ── touch swipe ──
  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = touchX.current - e.changedTouches[0].clientX;
    if (Math.abs(dx) > 40) dx > 0 ? goNext() : goPrev();
    touchX.current = null;
  };
 
  // ── compute translateX from measured step ──
  // The track starts at position 0 (the lastClone) offset left by `pos * step`.
  // We also shift left by 1 step on mount so the FIRST real slide is flush-left.
  const [step, setStep] = useState(0);
  useEffect(() => {
    const measure = () => setStep(getStep());
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [getStep]);
 
  const translateX = step ? -(pos * step) : undefined;
 
  return (
    <section className="projekte-mobile-carousel">
      <div
        className="projekte-mobile-track-wrap"
        ref={wrapRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="projekte-mobile-track"
          style={{
            transform: translateX !== undefined ? `translateX(${translateX}px)` : undefined,
            transition: anim ? "transform 0.55s cubic-bezier(0.16, 1, 0.3, 1)" : "none",
          }}
          onTransitionEnd={onTransitionEnd}
        >
          {extendedItems.map((item, i) => {
            // Label shown inside the slide footer
            const displayIdx = i === 0 ? total - 1 : i === total + 1 ? 0 : i - 1;
            const isClone = i === 0 || i === total + 1;
 
            return (
              <div
                className="projekte-mobile-slide"
                key={`sl-${i}`}
                ref={i === 1 ? slideRef : null}   // measure the first real slide
                aria-hidden={isClone || i !== pos}
              >
                <img
                  src={withPublic(item.imgSrc)}
                  alt={item.alt}
                  loading={i <= 2 ? "eager" : "lazy"}
                />
                <div className="projekte-mobile-slide-footer">
                  <span className="projekte-mobile-slide-num">
                    {String(displayIdx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                  </span>
                  <p className="projekte-mobile-slide-alt">{item.alt}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
 
      {/* Prev / Next */}
      <div className="projekte-mobile-controls">
        <button className="projekte-mobile-btn" onClick={goPrev} aria-label="Zurück">←</button>
        <button className="projekte-mobile-btn" onClick={goNext} aria-label="Weiter">→</button>
      </div>
 
      {/* Dots — only one per REAL slide */}
      <div className="projekte-mobile-dots" role="tablist" aria-label="Slide-Navigation">
        {mobileItems.map((_, i) => (
          <button
            key={i}
            className={`projekte-dot${i === realIndex ? " active" : ""}`}
            onClick={() => moveTo(i + 1)}
            role="tab"
            aria-selected={i === realIndex}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
 
// ── Stat Bar ───────────────────────────────────────────
function StatBar() {
  const ref = useRef(null);
  const inView = useInView(ref);
 
  const stats = [
    { number: "15+", label: "Projekte umgesetzt" },
    { number: "3+", label: "Jahre Erfahrung" },
    { number: "97%", label: "Zufriedene Kunden" },
  ];
 
  
}
 
// ── Main Component ─────────────────────────────────────
export default function Projekte() {
  const desktopItems = carouselItems.filter((it) => it.showOn === "desktop");
  const [lightboxIndex, setLightboxIndex] = useState(null);
 
  const openLightbox = useCallback((i) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() =>
    setLightboxIndex((i) => (i - 1 + desktopItems.length) % desktopItems.length), [desktopItems.length]);
  const nextImage = useCallback(() =>
    setLightboxIndex((i) => (i + 1) % desktopItems.length), [desktopItems.length]);
 
  return (
    <section className="projekte-section" id="projekte" aria-label="Unsere Projekte">
      <ProjekteHero />
 
      {/* Desktop: Masonry + Filters */}
      <MasonryGrid items={carouselItems} onCardClick={openLightbox} />
 
      {/* Mobile: Swipeable Carousel */}
      <MobileCarousel items={carouselItems} />
 
      {/* Stats */}
      <StatBar />
 
      {/* Lightbox */}
      <Lightbox
        items={desktopItems}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onPrev={prevImage}
        onNext={nextImage}
      />
    </section>
  );
}
 